import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const tokenMissing = !token || token.length < 20;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg("");
    if (password.length < 8) {
      setErrorMsg("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErrorMsg("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/auth/reset-password", { token, password });
      navigate("/app", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 400) {
          const code = err.code;
          if (code === "INVALID_TOKEN" || code === "TOKEN_USED") {
            setErrorMsg(
              "This reset link is invalid or already used. Request a new one."
            );
          } else if (code === "TOKEN_EXPIRED") {
            setErrorMsg("This reset link expired. Request a new one.");
          } else {
            setErrorMsg("Please check the form and try again.");
          }
        } else {
          setErrorMsg(`Something went wrong (${err.status}).`);
        }
      } else {
        setErrorMsg(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-50 px-6">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="mb-8 block text-center text-lg font-semibold text-ink-900 no-underline"
        >
          BrandEcho
        </Link>
        <div className="card">
          <h1 className="text-xl font-semibold text-ink-900">
            Choose a new password
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Pick something at least 8 characters. You'll be signed in
            automatically once it's saved.
          </p>
          {tokenMissing ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg border border-amber-200/70 bg-amber-50/60 px-3.5 py-3 text-sm text-amber-900">
                This link is missing a valid token. Request a new reset link to
                continue.
              </div>
              <Link
                to="/forgot-password"
                className="btn-primary w-full no-underline"
              >
                Request new link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-700">
                  New password
                </label>
                <input
                  className="input"
                  type="password"
                  required
                  minLength={8}
                  autoFocus
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-700">
                  Confirm new password
                </label>
                <input
                  className="input"
                  type="password"
                  required
                  minLength={8}
                  placeholder="Re-enter password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={busy}
              >
                {busy ? "Saving…" : "Reset password"}
              </button>
              {errorMsg ? (
                <p className="text-sm text-red-600">{errorMsg}</p>
              ) : null}
              <Link
                to="/login"
                className="block text-center text-sm text-ink-600 no-underline hover:text-ink-900 hover:underline"
              >
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
